import React, { useState, useEffect, useCallback, useRef } from 'react';
import ganttApi from '../services/gantt';
import './GanttProjet.css';

const STATUTS = [
  { id: 'a_faire', label: 'A faire', color: '#9ca3af' },
  { id: 'en_cours', label: 'En cours', color: '#3b82f6' },
  { id: 'fait', label: 'Fait', color: '#10b981' },
  { id: 'bloque', label: 'Bloque', color: '#ef4444' },
];

const RISQUE_STATUTS = [
  { id: 'ouvert', label: 'Ouvert', color: '#ef4444' },
  { id: 'surveille', label: 'Surveille', color: '#f59e0b' },
  { id: 'clos', label: 'Clos', color: '#10b981' },
];

const PHASE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function diffDays(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function toISO(d) {
  if (!d) return null;
  return new Date(d).toISOString().substring(0, 10);
}

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return toISO(d);
}

function generateWeeks(startDate, count) {
  const weeks = [];
  let d = new Date(mondayOf(startDate));
  for (let i = 0; i < count; i++) {
    weeks.push({ label: `S${i + 1}`, start: toISO(d), end: toISO(addDays(d, 6)) });
    d = addDays(d, 7);
  }
  return weeks;
}

function getDefaultTrame(dateDebut) {
  const d = new Date(dateDebut);
  const s = (w, day) => toISO(addDays(d, (w - 1) * 7 + day));
  return {
    phases: [
      {
        nom: 'Diagnostic & Cadrage', couleur: '#3b82f6', ordre: 0,
        actions: [
          { nom: 'Audit situation actuelle', date_debut: s(1, 0), date_fin: s(2, 4), statut: 'a_faire', ordre: 0 },
          { nom: 'Cadrage du perimetre', date_debut: s(2, 0), date_fin: s(3, 4), statut: 'a_faire', ordre: 1 },
          { nom: 'Validation du diagnostic', date_debut: s(3, 4), date_fin: s(3, 4), statut: 'a_faire', jalon: true, ordre: 2 },
        ],
      },
      {
        nom: 'Plan d\'action', couleur: '#10b981', ordre: 1,
        actions: [
          { nom: 'Redaction du plan', date_debut: s(4, 0), date_fin: s(5, 4), statut: 'a_faire', ordre: 0 },
          { nom: 'Consultation parties prenantes', date_debut: s(5, 0), date_fin: s(6, 4), statut: 'a_faire', vigilance: true, ordre: 1 },
          { nom: 'Validation plan d\'action', date_debut: s(6, 4), date_fin: s(6, 4), statut: 'a_faire', jalon: true, ordre: 2 },
        ],
      },
      {
        nom: 'Mise en oeuvre', couleur: '#f59e0b', ordre: 2,
        actions: [
          { nom: 'Execution mesures prioritaires', date_debut: s(7, 0), date_fin: s(9, 4), statut: 'a_faire', ordre: 0 },
          { nom: 'Suivi indicateurs', date_debut: s(8, 0), date_fin: s(11, 4), statut: 'a_faire', ordre: 1 },
          { nom: 'Bilan intermediaire', date_debut: s(10, 0), date_fin: s(10, 4), statut: 'a_faire', vigilance: true, ordre: 2 },
        ],
      },
    ],
    risques: [
      { sujet: 'Resistance au changement des equipes', statut: 'ouvert' },
      { sujet: 'Delai reglementaire consultation CSE', statut: 'surveille' },
    ],
  };
}

export default function GanttProjet({ user, isAdmin }) {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Projet
  const [projets, setProjets] = useState([]);
  const [projetId, setProjetId] = useState(null);
  const [projet, setProjet] = useState(null);
  const [phases, setPhases] = useState([]);
  const [actions, setActions] = useState([]);
  const [dependances, setDependances] = useState([]);
  const [risques, setRisques] = useState([]);

  // UI
  const [editingAction, setEditingAction] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [editingNom, setEditingNom] = useState(false);
  const [nomVal, setNomVal] = useState('');

  // Drag
  const dragRef = useRef(null);
  const gridRef = useRef(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load projets list
  useEffect(() => {
    ganttApi.getProjets().then(r => {
      setProjets(r.data);
      if (r.data.length > 0) setProjetId(r.data[0].id);
    }).catch(() => {});
  }, []);

  // Load full project
  const loadProjet = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await ganttApi.getProjetFull(id);
      setProjet(r.data);
      setPhases(r.data.phases || []);
      setActions(r.data.actions || []);
      setDependances(r.data.dependances || []);
      setRisques(r.data.risques || []);
      // Restore collapsed state
      const saved = localStorage.getItem(`gantt_collapsed_${id}`);
      if (saved) setCollapsed(JSON.parse(saved));
    } catch { showToast('Erreur chargement', 'error'); }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { loadProjet(projetId); }, [projetId, loadProjet]);

  // Save collapsed state
  useEffect(() => {
    if (projetId) localStorage.setItem(`gantt_collapsed_${projetId}`, JSON.stringify(collapsed));
  }, [collapsed, projetId]);

  // Auto-save helpers
  const saveAction = async (id, data) => {
    try {
      await ganttApi.updateAction(id, data);
      await loadProjet(projetId);
    } catch { showToast('Erreur sauvegarde', 'error'); }
  };

  const savePhase = async (id, data) => {
    try {
      await ganttApi.updatePhase(id, data);
      await loadProjet(projetId);
    } catch { showToast('Erreur sauvegarde', 'error'); }
  };

  const saveProjetField = async (data) => {
    try {
      await ganttApi.updateProjet(projetId, data);
      await loadProjet(projetId);
    } catch { showToast('Erreur sauvegarde', 'error'); }
  };

  // Create new project with default trame
  const handleNewProjet = async () => {
    try {
      const dateDebut = new Date().toISOString().substring(0, 10);
      const r = await ganttApi.createProjet({ nom: 'Nouveau projet', date_debut: dateDebut, nb_semaines: 12 });
      const trame = getDefaultTrame(dateDebut);
      await ganttApi.importProjet(r.data.id, { nom: 'Nouveau projet', date_debut: dateDebut, nb_semaines: 12, ...trame });
      const list = await ganttApi.getProjets();
      setProjets(list.data);
      setProjetId(r.data.id);
      showToast('Projet cree avec trame d\'exemple');
    } catch { showToast('Erreur creation', 'error'); }
  };

  const handleDeleteProjet = async () => {
    if (!projetId || !window.confirm('Supprimer ce projet et toutes ses donnees ?')) return;
    try {
      await ganttApi.deleteProjet(projetId);
      const list = await ganttApi.getProjets();
      setProjets(list.data);
      setProjetId(list.data.length > 0 ? list.data[0].id : null);
      if (list.data.length === 0) { setProjet(null); setPhases([]); setActions([]); setRisques([]); }
      showToast('Projet supprime');
    } catch { showToast('Erreur suppression', 'error'); }
  };

  // Phases
  const handleAddPhase = async () => {
    try {
      const couleur = PHASE_COLORS[phases.length % PHASE_COLORS.length];
      await ganttApi.createPhase({ projet_id: projetId, nom: 'Nouvelle phase', couleur, ordre: phases.length });
      await loadProjet(projetId);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleDeletePhase = async (phaseId) => {
    const phaseActions = actions.filter(a => a.phase_id === phaseId);
    if (phaseActions.length > 0 && !window.confirm(`Cette phase contient ${phaseActions.length} action(s). Supprimer ?`)) return;
    try {
      await ganttApi.deletePhase(phaseId);
      setEditingPhase(null);
      await loadProjet(projetId);
      showToast('Phase supprimee');
    } catch { showToast('Erreur', 'error'); }
  };

  // Actions
  const handleAddAction = async (phaseId) => {
    try {
      const dateDebut = projet?.date_debut || new Date().toISOString().substring(0, 10);
      const phaseActions = actions.filter(a => a.phase_id === phaseId);
      await ganttApi.createAction({
        phase_id: phaseId, nom: 'Nouvelle action',
        date_debut: dateDebut, date_fin: toISO(addDays(dateDebut, 4)),
        ordre: phaseActions.length,
      });
      await loadProjet(projetId);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleDeleteAction = async (actionId) => {
    try {
      await ganttApi.deleteAction(actionId);
      setEditingAction(null);
      await loadProjet(projetId);
      showToast('Action supprimee');
    } catch { showToast('Erreur', 'error'); }
  };

  // Dépendances
  const handleAddDep = async (actionId, depId) => {
    try {
      await ganttApi.createDependance({ action_id: actionId, depends_on_id: depId });
      await loadProjet(projetId);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleRemoveDep = async (depDbId) => {
    try {
      await ganttApi.deleteDependance(depDbId);
      await loadProjet(projetId);
    } catch { showToast('Erreur', 'error'); }
  };

  // Risques
  const handleAddRisque = async () => {
    try {
      await ganttApi.createRisque({ projet_id: projetId, sujet: 'Nouveau risque' });
      await loadProjet(projetId);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleDeleteRisque = async (id) => {
    try {
      await ganttApi.deleteRisque(id);
      await loadProjet(projetId);
    } catch { showToast('Erreur', 'error'); }
  };

  // Export / Import
  const handleExport = async () => {
    try {
      const r = await ganttApi.exportProjet(projetId);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `gantt-${projet?.nom || 'projet'}.json`; a.click();
      URL.revokeObjectURL(url);
      showToast('Trame exportee');
    } catch { showToast('Erreur export', 'error'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await ganttApi.importProjet(projetId, data);
      await loadProjet(projetId);
      showToast('Trame importee');
    } catch { showToast('Erreur import (fichier invalide ?)', 'error'); }
    e.target.value = '';
  };

  const handleReset = async () => {
    if (!window.confirm('Reinitialiser le projet avec la trame d\'exemple ?')) return;
    const dateDebut = projet?.date_debut || new Date().toISOString().substring(0, 10);
    const trame = getDefaultTrame(dateDebut);
    try {
      await ganttApi.importProjet(projetId, { nom: projet?.nom || 'Projet', date_debut: dateDebut, nb_semaines: projet?.nb_semaines || 12, ...trame });
      await loadProjet(projetId);
      showToast('Projet reinitialise');
    } catch { showToast('Erreur', 'error'); }
  };

  // Propose dependency shift
  const proposeDependencyShift = async (movedActionId, daysDelta) => {
    if (daysDelta === 0) return;
    const deps = dependances.filter(d => d.depends_on_id === movedActionId);
    if (deps.length === 0) return;
    const depActions = deps.map(d => actions.find(a => a.id === d.action_id)).filter(a => a && !a.verrouille);
    if (depActions.length === 0) return;
    const names = depActions.map(a => a.nom).join(', ');
    if (!window.confirm(`Decaler les actions dependantes (${names}) de ${daysDelta > 0 ? '+' : ''}${daysDelta} jour(s) ?`)) return;
    for (const a of depActions) {
      await saveAction(a.id, {
        date_debut: a.date_debut ? toISO(addDays(a.date_debut, daysDelta)) : null,
        date_fin: a.date_fin ? toISO(addDays(a.date_fin, daysDelta)) : null,
      });
    }
  };

  // ════════════════════════════════════════════════════════
  // DRAG & DROP
  // ════════════════════════════════════════════════════════
  const handleBarMouseDown = (e, action, mode) => {
    if (!isAdmin || action.verrouille) return;
    e.preventDefault();
    e.stopPropagation();
    const grid = gridRef.current;
    if (!grid) return;
    const rect = grid.getBoundingClientRect();
    const weeks = generateWeeks(projet.date_debut, projet.nb_semaines);
    const totalDays = weeks.length * 7;
    const colWidth = grid.scrollWidth / weeks.length;
    const dayWidth = colWidth / 7;

    dragRef.current = {
      actionId: action.id,
      mode, // 'move' or 'resize-end'
      startX: e.clientX,
      origStart: action.date_debut,
      origEnd: action.date_fin,
      dayWidth,
      totalDays,
      gridLeft: rect.left - grid.scrollLeft,
    };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const daysDelta = Math.round(dx / dragRef.current.dayWidth);
      if (daysDelta === 0) return;

      if (dragRef.current.mode === 'move') {
        const newStart = toISO(addDays(dragRef.current.origStart, daysDelta));
        const newEnd = toISO(addDays(dragRef.current.origEnd, daysDelta));
        setActions(prev => prev.map(a => a.id === dragRef.current.actionId ? { ...a, date_debut: newStart, date_fin: newEnd } : a));
      } else {
        const newEnd = toISO(addDays(dragRef.current.origEnd, daysDelta));
        if (new Date(newEnd) >= new Date(dragRef.current.origStart)) {
          setActions(prev => prev.map(a => a.id === dragRef.current.actionId ? { ...a, date_fin: newEnd } : a));
        }
      }
    };

    const onUp = async () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!dragRef.current) return;
      const a = actions.find(x => x.id === dragRef.current.actionId);
      if (!a) return;

      const daysDelta = diffDays(dragRef.current.origEnd, a.date_fin);
      if (a.date_debut !== dragRef.current.origStart || a.date_fin !== dragRef.current.origEnd) {
        await saveAction(a.id, { date_debut: a.date_debut, date_fin: a.date_fin });
        if (dragRef.current.mode === 'move') {
          await proposeDependencyShift(a.id, diffDays(dragRef.current.origStart, a.date_debut));
        } else {
          await proposeDependencyShift(a.id, daysDelta);
        }
      }
      dragRef.current = null;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════

  if (!projetId && projets.length === 0) {
    return (
      <div className="gantt-container">
        <div className="gantt-empty">
          <h2>Gestion de projet</h2>
          <p>Aucun projet. Creez-en un pour demarrer avec une trame d'exemple.</p>
          {isAdmin && <button className="gantt-btn-primary" onClick={handleNewProjet}>Creer un projet</button>}
        </div>
      </div>
    );
  }

  if (loading || !projet) {
    return <div className="gantt-container"><div className="gantt-loading">Chargement...</div></div>;
  }

  const weeks = generateWeeks(projet.date_debut, projet.nb_semaines);
  const projectStart = new Date(mondayOf(projet.date_debut));
  const totalDays = weeks.length * 7;

  const getBarStyle = (action) => {
    if (!action.date_debut) return null;
    const start = diffDays(projectStart, new Date(action.date_debut));
    const end = action.date_fin ? diffDays(projectStart, new Date(action.date_fin)) + 1 : start + 1;
    const left = Math.max(0, (start / totalDays) * 100);
    const width = Math.max(0.5, ((end - start) / totalDays) * 100);
    return { left: `${left}%`, width: `${width}%` };
  };

  const getPhaseBarStyle = (phaseId) => {
    const phaseActions = actions.filter(a => a.phase_id === phaseId && a.date_debut);
    if (phaseActions.length === 0) return null;
    const minDate = phaseActions.reduce((m, a) => (!m || a.date_debut < m ? a.date_debut : m), null);
    const maxDate = phaseActions.reduce((m, a) => {
      const end = a.date_fin || a.date_debut;
      return !m || end > m ? end : m;
    }, null);
    const start = diffDays(projectStart, new Date(minDate));
    const end = diffDays(projectStart, new Date(maxDate)) + 1;
    const left = Math.max(0, (start / totalDays) * 100);
    const width = Math.max(0.5, ((end - start) / totalDays) * 100);
    return { left: `${left}%`, width: `${width}%` };
  };

  const statutColor = (s) => STATUTS.find(x => x.id === s)?.color || '#9ca3af';

  const editAction = editingAction ? actions.find(a => a.id === editingAction) : null;
  const editPhase = editingPhase ? phases.find(p => p.id === editingPhase) : null;

  return (
    <div className="gantt-container">
      {toast && <div className={`gantt-toast ${toast.type}`}>{toast.msg}</div>}

      {/* En-tête projet */}
      <div className="gantt-header">
        <div className="gantt-header-left">
          {projets.length > 1 && (
            <select className="gantt-select" value={projetId} onChange={e => setProjetId(Number(e.target.value))}>
              {projets.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
            </select>
          )}
          {editingNom ? (
            <input className="gantt-nom-input" value={nomVal} autoFocus
              onChange={e => setNomVal(e.target.value)}
              onBlur={() => { saveProjetField({ nom: nomVal }); setEditingNom(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { saveProjetField({ nom: nomVal }); setEditingNom(false); }}}
            />
          ) : (
            <h2 className="gantt-nom" onClick={() => { if (isAdmin) { setNomVal(projet.nom); setEditingNom(true); } }}>
              {projet.nom}
            </h2>
          )}
        </div>
        <div className="gantt-header-right">
          <label className="gantt-field">
            <span>Debut</span>
            <input type="date" value={projet.date_debut?.substring(0, 10) || ''} disabled={!isAdmin}
              onChange={e => saveProjetField({ date_debut: e.target.value })} />
          </label>
          <label className="gantt-field">
            <span>Semaines</span>
            <input type="number" min="4" max="52" value={projet.nb_semaines || 12} disabled={!isAdmin}
              onChange={e => saveProjetField({ nb_semaines: parseInt(e.target.value) || 12 })} />
          </label>
          {isAdmin && (
            <>
              <button className="gantt-btn" onClick={handleExport}>Exporter</button>
              <label className="gantt-btn">
                Importer
                <input type="file" accept=".json" onChange={handleImport} hidden />
              </label>
              <button className="gantt-btn" onClick={handleReset}>Reinitialiser</button>
              <button className="gantt-btn gantt-btn-new" onClick={handleNewProjet}>+ Projet</button>
              <button className="gantt-btn gantt-btn-danger" onClick={handleDeleteProjet}>Supprimer</button>
            </>
          )}
        </div>
      </div>

      {/* Légende */}
      <div className="gantt-legend">
        {STATUTS.map(s => (
          <span key={s.id} className="gantt-legend-item">
            <span className="gantt-legend-dot" style={{ background: s.color }}></span>{s.label}
          </span>
        ))}
        <span className="gantt-legend-item"><span className="gantt-legend-diamond">&#9670;</span> Jalon</span>
        <span className="gantt-legend-item"><span className="gantt-legend-vig">!</span> Vigilance</span>
      </div>

      {/* Grille Gantt */}
      <div className="gantt-grid-wrapper">
        {/* Colonne gauche */}
        <div className="gantt-labels">
          <div className="gantt-labels-header">Actions</div>
          {phases.map(phase => {
            const isCollapsed = collapsed[phase.id];
            const phaseActions = actions.filter(a => a.phase_id === phase.id);
            return (
              <React.Fragment key={phase.id}>
                <div className="gantt-label-phase" style={{ borderLeftColor: phase.couleur }}
                  onClick={() => setCollapsed(prev => ({ ...prev, [phase.id]: !prev[phase.id] }))}>
                  <span className="gantt-chevron">{isCollapsed ? '▶' : '▼'}</span>
                  <span className="gantt-phase-name" onClick={e => { e.stopPropagation(); if (isAdmin) setEditingPhase(phase.id); }}>
                    {phase.nom}
                  </span>
                  <span className="gantt-phase-count">{phaseActions.length}</span>
                </div>
                {!isCollapsed && phaseActions.map(a => (
                  <div key={a.id} className={`gantt-label-action ${editingAction === a.id ? 'active' : ''}`}
                    onClick={() => setEditingAction(a.id)}>
                    {a.vigilance && <span className="gantt-vig-dot" title="Vigilance">!</span>}
                    {a.verrouille && <span className="gantt-lock" title="Verrouille">&#128274;</span>}
                    <span className="gantt-action-name">{a.nom}</span>
                  </div>
                ))}
                {!isCollapsed && isAdmin && (
                  <div className="gantt-label-add" onClick={() => handleAddAction(phase.id)}>+ Action</div>
                )}
              </React.Fragment>
            );
          })}
          {isAdmin && (
            <div className="gantt-label-add gantt-label-add-phase" onClick={handleAddPhase}>+ Nouvelle phase</div>
          )}
        </div>

        {/* Grille timeline */}
        <div className="gantt-timeline-wrapper">
          <div className="gantt-timeline-header">
            {weeks.map((w, i) => (
              <div key={i} className="gantt-week-col" title={`${formatDate(w.start)} - ${formatDate(w.end)}`}>
                {w.label}
              </div>
            ))}
          </div>
          <div className="gantt-timeline-body" ref={gridRef}>
            {phases.map(phase => {
              const isCollapsed = collapsed[phase.id];
              const phaseActions = actions.filter(a => a.phase_id === phase.id);
              const phaseBar = getPhaseBarStyle(phase.id);
              return (
                <React.Fragment key={phase.id}>
                  {/* Phase row */}
                  <div className="gantt-row gantt-row-phase" style={{ backgroundColor: phase.couleur + '0d' }}>
                    <div className="gantt-week-grid">
                      {weeks.map((_, i) => <div key={i} className="gantt-week-cell" />)}
                    </div>
                    {phaseBar && (
                      <div className="gantt-bar gantt-bar-phase" style={{ ...phaseBar, backgroundColor: phase.couleur + '40', borderColor: phase.couleur }} />
                    )}
                  </div>
                  {/* Action rows */}
                  {!isCollapsed && phaseActions.map(a => {
                    const bar = getBarStyle(a);
                    return (
                      <div key={a.id} className={`gantt-row gantt-row-action ${editingAction === a.id ? 'active' : ''}`}>
                        <div className="gantt-week-grid">
                          {weeks.map((_, i) => <div key={i} className="gantt-week-cell" />)}
                        </div>
                        {bar && !a.jalon && (
                          <div className="gantt-bar gantt-bar-action"
                            style={{ ...bar, backgroundColor: statutColor(a.statut) }}
                            onMouseDown={e => handleBarMouseDown(e, a, 'move')}
                            onClick={() => setEditingAction(a.id)}>
                            <div className="gantt-bar-resize" onMouseDown={e => handleBarMouseDown(e, a, 'resize-end')} />
                          </div>
                        )}
                        {bar && a.jalon && (
                          <div className="gantt-jalon" style={{ left: bar.left, color: '#f59e0b' }}
                            onClick={() => setEditingAction(a.id)}>&#9670;</div>
                        )}
                      </div>
                    );
                  })}
                  {/* Add action placeholder row */}
                  {!isCollapsed && isAdmin && <div className="gantt-row gantt-row-add" />}
                </React.Fragment>
              );
            })}
            {isAdmin && <div className="gantt-row gantt-row-add" />}
          </div>
        </div>
      </div>

      {/* Panneau édition action */}
      {editAction && (
        <div className="gantt-panel-overlay" onClick={() => setEditingAction(null)}>
          <div className="gantt-panel" onClick={e => e.stopPropagation()}>
            <div className="gantt-panel-header">
              <h3>Action</h3>
              <button className="gantt-panel-close" onClick={() => setEditingAction(null)}>&#x2715;</button>
            </div>
            <div className="gantt-panel-body">
              <label>Nom
                <input value={editAction.nom} onChange={e => {
                  const v = e.target.value;
                  setActions(prev => prev.map(a => a.id === editAction.id ? { ...a, nom: v } : a));
                }} onBlur={() => saveAction(editAction.id, { nom: editAction.nom })} />
              </label>
              <label>Phase
                <select value={editAction.phase_id} onChange={e => saveAction(editAction.id, { phase_id: Number(e.target.value) })}>
                  {phases.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              </label>
              <div className="gantt-panel-row">
                <label>Debut
                  <input type="date" value={editAction.date_debut?.substring(0, 10) || ''}
                    onChange={e => saveAction(editAction.id, { date_debut: e.target.value })} />
                </label>
                <label>Fin
                  <input type="date" value={editAction.date_fin?.substring(0, 10) || ''}
                    onChange={e => {
                      const oldEnd = editAction.date_fin;
                      const newEnd = e.target.value;
                      saveAction(editAction.id, { date_fin: newEnd }).then(() => {
                        if (oldEnd && newEnd) proposeDependencyShift(editAction.id, diffDays(oldEnd, newEnd));
                      });
                    }} />
                </label>
              </div>
              <label>Statut
                <select value={editAction.statut} onChange={e => saveAction(editAction.id, { statut: e.target.value })}>
                  {STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <label>Responsable
                <input value={editAction.responsable || ''} onChange={e => {
                  const v = e.target.value;
                  setActions(prev => prev.map(a => a.id === editAction.id ? { ...a, responsable: v } : a));
                }} onBlur={() => saveAction(editAction.id, { responsable: editAction.responsable })} />
              </label>
              <div className="gantt-panel-checks">
                <label className="gantt-check">
                  <input type="checkbox" checked={editAction.jalon || false}
                    onChange={e => saveAction(editAction.id, { jalon: e.target.checked })} /> Jalon
                </label>
                <label className="gantt-check">
                  <input type="checkbox" checked={editAction.vigilance || false}
                    onChange={e => saveAction(editAction.id, { vigilance: e.target.checked })} /> Vigilance
                </label>
                <label className="gantt-check">
                  <input type="checkbox" checked={editAction.verrouille || false}
                    onChange={e => saveAction(editAction.id, { verrouille: e.target.checked })} /> Verrouille
                </label>
              </div>
              <label>Notes
                <textarea rows={3} value={editAction.notes || ''} onChange={e => {
                  const v = e.target.value;
                  setActions(prev => prev.map(a => a.id === editAction.id ? { ...a, notes: v } : a));
                }} onBlur={() => saveAction(editAction.id, { notes: editAction.notes })} />
              </label>
              <div className="gantt-panel-deps">
                <span className="gantt-panel-deps-title">Dependances (fin → debut)</span>
                {dependances.filter(d => d.action_id === editAction.id).map(d => {
                  const depAction = actions.find(a => a.id === d.depends_on_id);
                  return (
                    <div key={d.id} className="gantt-dep-item">
                      <span>{depAction?.nom || '?'}</span>
                      {isAdmin && <button onClick={() => handleRemoveDep(d.id)}>&#x2715;</button>}
                    </div>
                  );
                })}
                {isAdmin && (
                  <select className="gantt-dep-add" value="" onChange={e => { if (e.target.value) handleAddDep(editAction.id, Number(e.target.value)); }}>
                    <option value="">+ Ajouter dependance...</option>
                    {actions.filter(a => a.id !== editAction.id && !dependances.some(d => d.action_id === editAction.id && d.depends_on_id === a.id))
                      .map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                  </select>
                )}
              </div>
              {isAdmin && (
                <button className="gantt-btn gantt-btn-danger gantt-btn-full" onClick={() => handleDeleteAction(editAction.id)}>
                  Supprimer cette action
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panneau édition phase */}
      {editPhase && (
        <div className="gantt-panel-overlay" onClick={() => setEditingPhase(null)}>
          <div className="gantt-panel gantt-panel-small" onClick={e => e.stopPropagation()}>
            <div className="gantt-panel-header">
              <h3>Phase</h3>
              <button className="gantt-panel-close" onClick={() => setEditingPhase(null)}>&#x2715;</button>
            </div>
            <div className="gantt-panel-body">
              <label>Nom
                <input value={editPhase.nom} onChange={e => {
                  const v = e.target.value;
                  setPhases(prev => prev.map(p => p.id === editPhase.id ? { ...p, nom: v } : p));
                }} onBlur={() => savePhase(editPhase.id, { nom: editPhase.nom })} />
              </label>
              <label>Couleur
                <div className="gantt-color-picker">
                  {PHASE_COLORS.map(c => (
                    <button key={c} className={`gantt-color-swatch ${editPhase.couleur === c ? 'active' : ''}`}
                      style={{ backgroundColor: c }}
                      onClick={() => savePhase(editPhase.id, { couleur: c })} />
                  ))}
                </div>
              </label>
              <label>Ordre
                <input type="number" value={editPhase.ordre || 0}
                  onChange={e => savePhase(editPhase.id, { ordre: parseInt(e.target.value) || 0 })} />
              </label>
              {isAdmin && (
                <button className="gantt-btn gantt-btn-danger gantt-btn-full" onClick={() => handleDeletePhase(editPhase.id)}>
                  Supprimer cette phase
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Risques & vigilance */}
      <div className="gantt-risques">
        <div className="gantt-risques-header">
          <h3>Risques & vigilance</h3>
          {isAdmin && <button className="gantt-btn" onClick={handleAddRisque}>+ Risque</button>}
        </div>
        {risques.length === 0 ? (
          <p className="gantt-risques-empty">Aucun risque identifie.</p>
        ) : (
          <table className="gantt-risques-table">
            <thead>
              <tr>
                <th>Sujet</th>
                <th>Statut</th>
                <th>Derniere revue</th>
                <th>Prochaine revue</th>
                <th>Notes</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {risques.map(r => (
                <tr key={r.id}>
                  <td>
                    {isAdmin ? (
                      <input value={r.sujet} className="gantt-risque-input"
                        onChange={e => { const v = e.target.value; setRisques(prev => prev.map(x => x.id === r.id ? { ...x, sujet: v } : x)); }}
                        onBlur={() => ganttApi.updateRisque(r.id, { sujet: r.sujet })} />
                    ) : r.sujet}
                  </td>
                  <td>
                    {isAdmin ? (
                      <select value={r.statut} className="gantt-risque-select"
                        style={{ color: RISQUE_STATUTS.find(s => s.id === r.statut)?.color }}
                        onChange={e => { ganttApi.updateRisque(r.id, { statut: e.target.value }); setRisques(prev => prev.map(x => x.id === r.id ? { ...x, statut: e.target.value } : x)); }}>
                        {RISQUE_STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    ) : (
                      <span style={{ color: RISQUE_STATUTS.find(s => s.id === r.statut)?.color, fontWeight: 600 }}>
                        {RISQUE_STATUTS.find(s => s.id === r.statut)?.label}
                      </span>
                    )}
                  </td>
                  <td>
                    {isAdmin ? (
                      <input type="date" value={r.date_revue?.substring(0, 10) || ''} className="gantt-risque-input"
                        onChange={e => { ganttApi.updateRisque(r.id, { date_revue: e.target.value || null }); setRisques(prev => prev.map(x => x.id === r.id ? { ...x, date_revue: e.target.value } : x)); }} />
                    ) : formatDate(r.date_revue)}
                  </td>
                  <td>
                    {isAdmin ? (
                      <input type="date" value={r.date_prochaine_revue?.substring(0, 10) || ''} className="gantt-risque-input"
                        onChange={e => { ganttApi.updateRisque(r.id, { date_prochaine_revue: e.target.value || null }); setRisques(prev => prev.map(x => x.id === r.id ? { ...x, date_prochaine_revue: e.target.value } : x)); }} />
                    ) : formatDate(r.date_prochaine_revue)}
                  </td>
                  <td>
                    {isAdmin ? (
                      <input value={r.notes || ''} className="gantt-risque-input"
                        onChange={e => { const v = e.target.value; setRisques(prev => prev.map(x => x.id === r.id ? { ...x, notes: v } : x)); }}
                        onBlur={() => ganttApi.updateRisque(r.id, { notes: r.notes })} />
                    ) : (r.notes || '-')}
                  </td>
                  {isAdmin && (
                    <td><button className="gantt-btn-icon gantt-btn-danger" onClick={() => handleDeleteRisque(r.id)}>&#x2715;</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
